import { css } from '@emotion/react';
import Tooltip from '@mui/material/Tooltip';

import { isAggregateRunState } from 'types/RunState';
import { buildBreakdownParts, statePriority, taskStateColor } from 'utils/taskStateColors';

export default function TaskStateBar({
  counts,
  onStateClick,
  selectedState,
}: {
  counts: Record<string, number>;
  onStateClick?: (state: string) => void;
  selectedState?: string;
}) {
  const entries = Object.entries(counts)
    .filter(([, count]) => count > 0)
    .sort(([a], [b]) => statePriority(a) - statePriority(b));

  if (entries.length === 0) {
    return null;
  }

  const breakdown = buildBreakdownParts(counts).join(', ');
  const hasFilterable = Boolean(onStateClick) && entries.some(([state]) => isAggregateRunState(state));

  return (
    <Tooltip title={breakdown}>
      <div
        role={hasFilterable ? 'group' : 'img'}
        aria-label={`Task states: ${breakdown}`}
        tabIndex={hasFilterable ? undefined : 0}
        onClick={(event) => event.stopPropagation()}
        css={css`
          display: flex;
          width: 120px;
          height: 10px;
          border-radius: 5px;
          overflow: hidden;
        `}
      >
        {entries.map(([state, count]) => {
          const color = taskStateColor(state);
          const selected = selectedState === state;
          if (!onStateClick || !isAggregateRunState(state)) {
            return (
              <div
                key={state}
                css={css`
                  min-width: 3px;
                `}
                style={{ flexGrow: count, backgroundColor: color }}
              />
            );
          }
          return (
            <button
              key={state}
              type="button"
              aria-pressed={selected}
              aria-label={`Filter workflows by ${state}`}
              title={selected ? `Clear ${state} filter` : `Filter workflows by ${state}`}
              onClick={() => onStateClick(state)}
              css={css`
                appearance: none;
                min-width: 3px;
                height: 100%;
                margin: 0;
                padding: 0;
                border: none;
                border-radius: 0;
                box-shadow: ${selected ? 'inset 0 0 0 1px #fff' : 'none'};
                cursor: pointer;
              `}
              style={{ flexGrow: count, backgroundColor: color }}
            />
          );
        })}
      </div>
    </Tooltip>
  );
}
